import { useCallback, useContext, useMemo, useState } from 'react'
import * as React from 'react'
import clsx from 'clsx'

import CornerControls from './common/CornerControls'
import { DetailCardAndPinLayout, DetailNoteCardLayout } from './common/DetailCardAndPinLayout'
import type { DetailItemCardVariant } from './common/types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { TripDocumentsContext } from 'tripPlanner/contexts/TripDocumentsContext'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import type { NoteItem } from 'tripPlanner/types/tripItem'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import Attachments from './common/Attachments'
import styled from 'styled-components'
import { rem } from 'polished'

const StyledTripItemInlineNoteEdit = styled(TripItemInlineNoteEdit)`
  /* This is for making note area in the same row with corner controls */
  width: calc(100% - ${rem(40)});
`

interface Props {
  item: NoteItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailNoteCard({ item, variant }: Props) {
  const canEdit = useCanEditTrip()
  const [inFocus, setInFocus] = useState(false)

  const { data: trip } = useTrip({ tripId: item.tripId })

  const { itemNumber, itemColor } = useMemo(
    () => getItemPinBadge(item, variant, trip),
    [item, variant, trip],
  )

  const documentContext = useContext(TripDocumentsContext)
  const attachedDocuments =
    documentContext?.documentsResult?.attachedFiles[item.id] ?? []

  const hasNoAttachments = !!documentContext?.documentsResult && attachedDocuments.length === 0

  const onFocus = useCallback(() => {
    setInFocus(true)
  }, [])

  const onBlur = useCallback(() => {
    setInFocus(false)
  }, [])

  return (
    <DetailCardAndPinLayout>
      <DetailCardAndPinLayout.Pin className="pin-anchor">
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </DetailCardAndPinLayout.Pin>
      <DetailNoteCardLayout className={clsx({ focus: inFocus })}>
        <DetailNoteCardLayout.Content>
          <CornerControls item={item} />
          <VerticalSpacer gap={12}>
            {canEdit && <StyledTripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              deleteItemOnClear={hasNoAttachments}
              inFocus={inFocus}
              onFocus={onFocus}
              onBlur={onBlur}
            />}
            {!canEdit && <TripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              deleteItemOnClear={hasNoAttachments}
              inFocus={inFocus}
              onFocus={onFocus}
              onBlur={onBlur}
            />}
            <Attachments item={item} />
          </VerticalSpacer>
        </DetailNoteCardLayout.Content>
      </DetailNoteCardLayout>
    </DetailCardAndPinLayout>
  )
}

export default DetailNoteCard
